import { useState, useEffect, Fragment } from "react"
import { NextSeo } from "next-seo"
import { useRouter } from "next/router"
import classNames from "classnames"
import image, { PRODUCT_PHOTOGRAMME } from "lacinetek-common/src/images"

import { useTranslation } from "lib/i18n/translation"
import { useMainCategories } from "lib/tools/categories"
import classification from "lib/tools/classification"
import { listComponent } from "lib/tools/categories"
import { getFrom } from "lib/redirects"

import ProductAccessLink from "components/common/product-access-link"
import SkeletonLink from "components/common/skeleton-link"
import Products from "components/common/products"
import Image from "components/common/image"
import { Menu } from "./menu"

export default ({ tops }) => {
	if (!tops) {
		return <div className="is-loading" />
	}

	const { c } = useTranslation("common")
	const router = useRouter()
	const [topsConfiguration, setTopsConfiguration] = useState(tops.map(t => t.conf))
	const [target, setTarget] = useState()
	const [min, setMin] = useState(5)

	const manageCurrentConfig = (url, min) => {
		let selectedConfiguration = topsConfiguration.find(conf => conf.target === url)
		if (selectedConfiguration) {
			selectedConfiguration.citations = tops
			.find(top => top.conf.title === selectedConfiguration.title).data
			.filter(c => c.count >= min)
		}
		return selectedConfiguration
	}

	const [currentConfiguration, setCurrentConfiguration] = useState(manageCurrentConfig(topsConfiguration[0].target, min))

	const onRouteChange = (url) => {
		const request = url.split("/")
		if (request.length == 3 && request[1] !== "") {
			setTarget(url.replace(`/${request[1]}`, ""))
		} else {
			setTarget(url)
		}
	}

	useEffect(() => {
		const url = getFrom(router.pathname)
		const request = url.split("/")
		if (request.length == 3 && request[1] !== "") {
			setTarget(url.replace(`/${target[1]}`, ""))
		} else {
			setTarget(url)
		}
	}, [])

	useEffect(() => {
		if (currentConfiguration) {
			setCurrentConfiguration(manageCurrentConfig(getFrom(currentConfiguration.target), min))
		}
	}, [min])

	useEffect(() => {
		if (target) {
			setCurrentConfiguration(manageCurrentConfig(getFrom(target), min))
		}
	}, [target])

	const showMore = () => {
		if (min > 1) setMin((min - 2) > 0 ? (min - 2) : 1)
	}

	return (
		<section key={currentConfiguration.title}>
			<NextSeo
				title={c(`${currentConfiguration.title}`)}
			/>
			<h1 className="main-title">{c("top-lists-strong")}</h1>
			<div id="films-top" className="results-container">
				<Menu links={topsConfiguration} current={currentConfiguration} />
				<p className="excerpt">{c(currentConfiguration.subtitle)}</p>
				<ul className="results list">
					{currentConfiguration.citations.map(({ count, products, directors }) => (
						<li key={`top-list-${count}-films`} >
							<h2 className="results-title">
								{c(`${currentConfiguration.title}-title`, { count })}
							</h2>
							<ul className={`grid ${currentConfiguration.gridClassName}`}>
								{
									products
									&& <Products products={products.map(id => ({ id }))}>
										{topProducts => (
											<>
												{topProducts.map((product, index) => {
													return (
														<Product
															key={`top-list-${count}-film-${index}`}
															product={product}
															recommenders={product.recommendersIds}
															available={product.available}
															inSelection={product.inMonthSelection}
															hasBonus={product.bonusesCount > 0}
														/>
													)
												})}
											</>
										)}
									</Products>
								}
								{directors && (directors.map(director => {
									return <Director director={director} />
								}))}
							</ul>
						</li>
					))}
				</ul>
				{
					min > 1
					&& <p className="centered">
						<a
							href="#"
							onClick={e => { e.preventDefault(); showMore() }}
							rel="nofollow noopener"
							title={c("show-more")}
							className="button icon-more"
						>
							{c("show-more")}
						</a>
					</p>
				}
			</div>
		</section>
	)
}

const Product = ({ product, recommenders, available, inSelection, hasBonus }) => {
	const { c } = useTranslation("common")
	const { allLists } = useMainCategories()

	return (
		<li className={`item has-extras ${inSelection ? "in-selection" : ""} ${available ? "" : "unavailable"}`}>
			<div className="item-content">
				<div className="film-link">
					<figure className="cover">
						<picture>
							<Image
								src={image(product, PRODUCT_PHOTOGRAMME)}
								alt={product.name}
							/>
						</picture>
					</figure>
					<div className="infos">
						<h3 className="title">
							{product.name}
							{classification(product)}
						</h3>
						<span className="director"> {c("of")} {product.director}</span>
						<span className="year">{product.year}</span>
						<span className="country">{product.origin}</span>
						<span className="duration">{product.duration}</span>
						{
							hasBonus
							&& <ul className="has-bonus">
								<li className="exclusive">{c("bonus")}</li>
							</ul>
						}
					</div>
					<div className="extras">
						<SkeletonLink href="/film/[id]" as={`/film/${product.linkRewrite}`}>
							<a
								title={c("see-single-film")}
								className="more-link"
							>
								<span className="label">{c("see-single-film")}</span>
							</a>
						</SkeletonLink>
						<p className={`recommended-by`}>
							{
								recommenders && recommenders.length > 0
								&& <>
									<b>{c("recommended-by")} </b>
									{recommenders.map((id, index) => {
										const list = allLists.find(l => l.director.id === id)
										if (!list) return null
										return (
											<Fragment key={`film-${product.id}-${list.name.replace(" | ", " ")}`}>
												<SkeletonLink
													href={`/lists/[id]`}
													as={`/lists/${c(list.linkRewrite)}`}
												>
													<a title={c("see-list")}>
														{list.name.replace(" | ", " ")}
													</a>
												</SkeletonLink>
												{index !== (recommenders.length - 1) ? " " : ""}
											</Fragment>
										)
									})}
								</>
							}
						</p>
					</div>
				</div>
			</div>
			<nav className="film-actions" aria-label={c("film-actions")}>
				<ProductAccessLink product={product} />
			</nav>
		</li>
	)
}

const Director = ({ director }) => {
	const { c } = useTranslation("common")

	return (
		<li className="item has-extras">
			<div className="item-content">
				<figure className="cover">
					<picture>
						<Image
							src={director.image}
							alt={director.name}
						/>
					</picture>
				</figure>
				<div className="infos">
					<h3 className="title-alt">
						{listComponent(director)}
					</h3>
				</div>
				<div className="extras">
					<SkeletonLink
						href={{
							pathname: "/films",
							query: {
								director: encodeURIComponent(JSON.stringify({
									ids: director.ids,
									name: director.name
								}))
							}
						}}
					>
						<a title={c("see-quoted-films")} className="more-link">
							<span className="label">{c("see-quoted-films")}</span>
						</a>
					</SkeletonLink>
					<p className="centered">
						<span className="button small">
							{c("see-quoted-films")}
						</span>
					</p>
				</div>
			</div>
		</li>
	)
}
